import { observer } from "mobx-react"
import { DEFAULT_LANG } from "@providers"
import { Page, Header, useStore, Footer, PureLogo } from "@features/common"
import { PostsGrid, ArticlePreviewClipPath } from "@features/journal"
import {
  SaferDatingLogoEN,
  IntroSection,
  FeaturedPostSection,
  BottomBannerSection,
  SaferDatingButton,
} from "@features/safer-dating"
import { scrollToTop } from "@lib/scroll"
import { RouterLink } from "@lib/routing"
import { IconHamburger } from "@lib/icons"
import { serverPath } from "@lib/server"
import { useSmartBanner } from "@features/smart-banner"
import { IconRefreshSaferDating } from "@lib/icons"

const SELF_URL = "safer-dating"

export async function getServerSideProps(ctx) {
  const Models = ctx.req.Models
  const { lang = "en" } = ctx.params

  // Forced: this page carries its own language list, so it can offer
  // languages the rest of the site does not. Same as /ua/guidelines.
  Models.GlobalModel.setLang(lang, true)

  try {
    await Models.GlobalModel.getClientCountryCode(ctx)
    await Models.SaferDatingModel.getInitialData()
    await Models.GlobalModel.getSmartBanner()

    return {
      props: Models.serialize(),
    }
  } catch (err) {
    if (lang === DEFAULT_LANG) {
      return {
        redirect: {
          destination: `/${lang}/not-found`,
          permanent: false,
        },
      }
    } else {
      return {
        redirect: {
          destination: `/${DEFAULT_LANG}/safer-dating`,
          permanent: false,
        },
      }
    }
  }
}

const SaferDatingPage = observer(function JournalPage() {
  const { GlobalModel, SaferDatingModel } = useStore()
  const { smartBanner } = useSmartBanner({
    link: SaferDatingModel.branchLinks.smart_banner_link,
    playmarketLink: SaferDatingModel.branchLinks.smart_banner_playmarket_link,
    appgalleryLink: SaferDatingModel.branchLinks.smart_banner_app_gallery_link,
  })

  const pageTitle = SaferDatingModel.getTemplate("title")
  const metaTitle = SaferDatingModel.getTemplate("metaTitle") || pageTitle
  const metaImage =
    new URL(GlobalModel.currentUrl).origin +
    serverPath("/static/images/logos/pure_safer_dating_preview.png")
  const pageDescription = SaferDatingModel.getTemplate("metaDescription")
  const seoH1 = SaferDatingModel.getTemplate("seoH1.title")

  const pageLanguages = SaferDatingModel.availableLanguages.length
    ? SaferDatingModel.availableLanguages
    : GlobalModel.accessLanguages

  const alternateLinks = pageLanguages.map((lang) => ({
    lang,
    url: `${GlobalModel.originUrl || ""}/${lang}/${SELF_URL}`,
  }))

  return (
    <Page
      meta={{
        title: pageTitle,
        description: pageDescription,
        metaTitle,
        metaImage,
        alternateLinks,
      }}
      smartBanner={smartBanner}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri={`/${SELF_URL}`}
              forceLang={GlobalModel.lang}
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3"
            >
              <div className="flex space-x-2 tablet-m:space-x-4">
                <PureLogo className="text-white h-4 w-auto my-1 tablet:w-auto tablet-m:h-7 tablet:h-7 tablet-m:my-1.5" />
                <SaferDatingLogoEN />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={GlobalModel.openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      alternativeHeader
      footer={
        <Footer
          googlePlayLink={SaferDatingModel.branchLinks.main_footer_google_play}
          appStoreLink={SaferDatingModel.branchLinks.main_footer_app_store}
          appGalleryLink={SaferDatingModel.branchLinks.main_footer_app_gallery}
          accessLanguages={pageLanguages}
        />
      }
      seoH1={seoH1}
    >
      <ArticlePreviewClipPath />
      <IntroSection
        title={SaferDatingModel.getTemplate("header.title")}
        description={SaferDatingModel.getTemplate("header.description")}
      />
      <FeaturedPostSection
        title={SaferDatingModel.featuredPost?.title}
        description={SaferDatingModel.featuredPost?.description}
        imageSrc={SaferDatingModel.featuredPost?.image_preview.url}
        buttonText={SaferDatingModel.getTemplate("featuredPost.button")}
        buttonHref={`/${SELF_URL}/${SaferDatingModel.featuredPost?.slug}`}
        buttonHrefLang={SaferDatingModel.featuredPost?.lang}
      />
      <div className="pt-9 tablet:pt-12">
        <PostsGrid
          dataModel="SaferDatingModel"
          postUrlPrefix={SELF_URL}
          hasRotation
          className="max-w-[1280px] mx-auto px-5 desktop-1.5xl:px-0"
          renderButton={(onClick) => (
            <SaferDatingButton
              renderAs="button"
              onClick={onClick}
              className="rotate-[-2deg]"
            >
              <IconRefreshSaferDating className="w-[16px] h-[16px] tablet-m:w-[22px] tablet-m:h-[22px] text-white mr-2" />
              <span>
                {SaferDatingModel.getTemplate("posts.showMoreButton")}
              </span>
            </SaferDatingButton>
          )}
        />
      </div>
      <BottomBannerSection
        title={SaferDatingModel.getTemplate("bottomBanner.title")}
        description={SaferDatingModel.getTemplate("bottomBanner.description")}
        buttonText={SaferDatingModel.getTemplate("bottomBanner.button")}
        buttonHref={SaferDatingModel.branchLinks.page_banner_button}
      />
    </Page>
  )
})

export default SaferDatingPage
